import { Add, Clear, Delete, Search, Timelapse } from '@mui/icons-material';
import {
  Box, Button, CircularProgress, IconButton, InputAdornment, List,
  ListItemButton, ListItemText, Pagination, Paper, Stack, TextField,
  Tooltip, Typography
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useSchoolShift } from '../hooks/useSchoolShift';

interface Props {
  state: ReturnType<typeof useSchoolShift>['state'];
  actions: ReturnType<typeof useSchoolShift>['actions'];
}

export default function SchoolShiftSidebar({ state, actions }: Props) {
  const { searchTerm, isLoadingList, isFetchingList, shiftList, selectedId, totalCount, pagination } = state;
  const { setSearchTerm, handleCreateNew, handleSelectShift, handleOpenDeleteModal, handleChangePage } = actions;
  const { t } = useTranslation();

  const totalPages = Math.max(1, Math.ceil(totalCount / pagination.pageSize));

  return (
    <Paper 
      elevation={0} 
      sx={{ 
        width: { xs: '100%', md: 320 }, 
        display: { xs: selectedId ? 'none' : 'flex', md: 'flex' },
        height: '100%',
        border: '1px solid', 
        borderColor: 'divider', 
        flexDirection: 'column', 
        borderRadius: 2, 
        overflow: 'hidden' 
      }}
    >
      <Box sx={{ p: 3, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
          <Timelapse color="primary" />
          <Typography variant="h6" fontWeight="bold">{t('schoolShiftPage.title')}</Typography>
        </Stack>
        <Stack direction="row" spacing={1}>
          <TextField 
            fullWidth size="small" placeholder={t('schoolShiftPage.searchPlaceholder')} 
            value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} 
            slotProps={{ 
                input: { 
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search fontSize="small" />
                    </InputAdornment>
                  ),
                  endAdornment: searchTerm ? (
                    <InputAdornment position="end">
                      <Tooltip title={t('schoolShiftPage.clearSearch')} arrow placement="top">
                        <IconButton size="small" onClick={() => setSearchTerm('')} edge="end">
                          <Clear fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  ) : null
                } 
            }} 
          />
          <Tooltip title={t('schoolShiftPage.newShift')} arrow placement="bottom">
            <Button variant="contained" sx={{ minWidth: 40, p: 0 }} onClick={handleCreateNew}>
              <Add />
            </Button>
          </Tooltip>
        </Stack>
      </Box>

      <List 
        sx={{ 
          flex: 1, 
          overflowY: 'auto', 
          scrollbarGutter: 'stable',
          p: 1,
          opacity: isFetchingList && !isLoadingList ? 0.4 : 1,
          transition: 'opacity 0.2s ease-in-out',
          pointerEvents: isFetchingList ? 'none' : 'auto'
        }}
      >
        {isLoadingList ? (
          <CircularProgress sx={{ display: 'block', m: '20px auto' }} />
        ) : shiftList.length === 0 ? (
          <Box sx={{ textAlign: 'center', mt: 6, px: 2 }}>
            <Typography variant="body2" color="text.secondary" fontWeight="bold">
              {searchTerm ? t('schoolShiftPage.noShiftFound') : t('schoolShiftPage.noShiftRegistered')}
            </Typography>
          </Box>
        ) : (
          shiftList.map((shift) => (
            <ListItemButton 
              key={shift.id} 
              selected={selectedId === shift.id} 
              onClick={() => handleSelectShift(shift.id)} 
              sx={{ borderRadius: 2, mb: 1, borderLeft: selectedId === shift.id ? '4px solid #2563eb' : '4px solid transparent' }}
            >
              <ListItemText 
                primary={
                  <span title={shift.name.length > 22 ? shift.name : undefined}>
                    {shift.name}
                  </span>
                } 
                secondary={`${shift.linkedMembersCount ?? 0} ${t('schoolShiftPage.members')}`} 
                slotProps={{ 
                    primary: { 
                      noWrap: true, 
                      fontWeight: selectedId === shift.id ? 'bold' : 'medium' 
                    } 
                }} 
                sx={{ m: 0, pr: 1 }} 
              />

              <IconButton size="small" edge="end" sx={{ opacity: 0.5, '&:hover': { color: 'error.main', opacity: 1 } }} onClick={(e) => handleOpenDeleteModal(e, shift)}>
                <Delete fontSize="small" />
              </IconButton>
            </ListItemButton>
          ))
        )}
      </List>

      <Box sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider', display: 'flex', justifyContent: 'center' }}>
        <Pagination 
          count={totalPages} 
          page={pagination.pageNumber} 
          onChange={handleChangePage} 
          color="primary" 
          size="small" 
          siblingCount={0}
          boundaryCount={1}
        />
      </Box>
    </Paper>
  );
}